/**
 * Web-side auth — companion to api/src/lib/auth.ts. Implements the SPA-friendly
 * pattern from CLAUDE.md §13: web sends `Authorization: Bearer <accessToken>`,
 * the API holds the refresh token (sealed) and mints fresh access tokens on
 * demand. No cross-origin cookies needed.
 *
 * Storage model:
 *   sessionStorage 'wrappa.auth.v1' = { accessToken, sealedSession, user, expiresAt }
 *   sessionStorage 'wrappa.auth.pending' = { state, returnTo }  // pre-callback
 *
 * Why sessionStorage and not localStorage: scoped to one tab, gone on tab close.
 * This is a deliberate trade-off — XSS risk is contained to the active tab,
 * and the user re-authenticates per session. The sealedSession blob is opaque,
 * so a leaked sessionStorage on its own can't refresh; only the API can unseal.
 */

import { createContext, useContext } from 'react';

const STORAGE_KEY = 'wrappa.auth.v1';
const PENDING_KEY = 'wrappa.auth.pending';

const CLIENT_ID = import.meta.env.VITE_WORKOS_CLIENT_ID ?? '';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:7071';

// AuthKit's hosted authorize endpoint. `provider=authkit` shows the unified
// hosted UI (email + every social provider enabled in the dashboard).
// To skip directly to a single provider, pass e.g. 'GoogleOAuth'.
const AUTHORIZE_URL = 'https://api.workos.com/user_management/authorize';

export interface AuthUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  profilePictureUrl: string | null;
}

export interface AuthSession {
  accessToken: string;
  sealedSession: string | null;
  user: AuthUser;
  /** Epoch ms when the access token should be considered stale. */
  expiresAt: number;
}

interface PendingState {
  state: string;
  returnTo: string;
}

/* -------------------------------------------------------------------------
 * Persistence
 * ------------------------------------------------------------------------- */

export function readSession(): AuthSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSession;
    if (
      typeof parsed.accessToken === 'string'
      && typeof parsed.expiresAt === 'number'
      && parsed.user
      && typeof parsed.user.id === 'string'
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

export function writeSession(session: AuthSession): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(PENDING_KEY);
}

/* -------------------------------------------------------------------------
 * Sign-in flow
 * ------------------------------------------------------------------------- */

/**
 * Redirect the browser to AuthKit's hosted login. The returnTo path is
 * stashed in sessionStorage and restored after the callback completes.
 */
export function startSignIn(returnTo: string = '/dashboard'): void {
  if (!CLIENT_ID) {
    throw new Error(
      'VITE_WORKOS_CLIENT_ID is not configured. Check web/.env.local.',
    );
  }
  const state = randomState();
  const pending: PendingState = { state, returnTo };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));

  const url = new URL(AUTHORIZE_URL);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', CLIENT_ID);
  // Derive the callback from the CURRENT origin so sign-in returns to whatever
  // domain the user started on (www.wrappa.app, the SWA URL, or localhost) —
  // not a build-time-baked URL. Each origin's /auth/callback must be registered
  // in the WorkOS dashboard.
  url.searchParams.set('redirect_uri', `${window.location.origin}/auth/callback`);
  url.searchParams.set('provider', 'authkit');
  url.searchParams.set('state', state);

  window.location.assign(url.toString());
}

function randomState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export interface CallbackResult {
  session: AuthSession;
  returnTo: string;
}

interface CallbackResponse {
  accessToken: string;
  sealedSession: string | null;
  user: AuthUser;
}

/**
 * Called by the /auth/callback page. Verifies the state nonce, exchanges
 * the code via the API, persists the resulting session.
 */
export async function completeSignIn(code: string, stateFromUrl: string): Promise<CallbackResult> {
  const rawPending = sessionStorage.getItem(PENDING_KEY);
  if (!rawPending) {
    throw new Error('No pending sign-in. Please start over.');
  }
  const pending = JSON.parse(rawPending) as PendingState;
  if (pending.state !== stateFromUrl) {
    sessionStorage.removeItem(PENDING_KEY);
    throw new Error('State mismatch. Sign-in cancelled for safety.');
  }
  sessionStorage.removeItem(PENDING_KEY);

  const res = await fetch(`${API_BASE_URL}/api/auth/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    throw new Error(`Sign-in failed (${res.status}). Please try again.`);
  }
  const body = (await res.json()) as CallbackResponse;
  const session: AuthSession = {
    accessToken: body.accessToken,
    sealedSession: body.sealedSession,
    user: body.user,
    // WorkOS access tokens default to ~5 minutes. Refresh proactively at 4.
    expiresAt: Date.now() + 4 * 60 * 1000,
  };
  writeSession(session);
  return { session, returnTo: pending.returnTo };
}

/* -------------------------------------------------------------------------
 * Token refresh
 * ------------------------------------------------------------------------- */

interface RefreshResponse {
  accessToken: string;
  sealedSession: string;
  user: AuthUser;
}

/**
 * Refresh using the sealed session. Returns null if the session can't be
 * refreshed (caller must show a sign-in prompt).
 */
export async function refreshSession(): Promise<AuthSession | null> {
  const current = readSession();
  if (!current?.sealedSession) return null;

  try {
    const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sealedSession: current.sealedSession }),
    });
    if (!res.ok) {
      clearSession();
      return null;
    }
    const body = (await res.json()) as RefreshResponse;
    const next: AuthSession = {
      accessToken: body.accessToken,
      sealedSession: body.sealedSession,
      user: body.user,
      expiresAt: Date.now() + 4 * 60 * 1000,
    };
    writeSession(next);
    return next;
  } catch {
    return null;
  }
}

/**
 * Returns a fresh access token, refreshing in-place if expired. null means
 * the caller must redirect to sign-in.
 */
export async function getValidAccessToken(): Promise<string | null> {
  const current = readSession();
  if (!current) return null;
  if (Date.now() < current.expiresAt) return current.accessToken;
  const refreshed = await refreshSession();
  return refreshed?.accessToken ?? null;
}

/* -------------------------------------------------------------------------
 * Sign-out
 * ------------------------------------------------------------------------- */

export async function signOut(returnTo: string = '/'): Promise<void> {
  const current = readSession();
  clearSession();

  if (!current?.sealedSession) {
    window.location.assign(returnTo);
    return;
  }

  try {
    const res = await fetch(`${API_BASE_URL}/api/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sealedSession: current.sealedSession,
        returnTo: new URL(returnTo, window.location.origin).toString(),
      }),
    });
    const body = (await res.json()) as { logoutUrl?: string };
    if (body.logoutUrl) {
      window.location.assign(body.logoutUrl);
      return;
    }
  } catch {
    // Fall through to local redirect.
  }
  window.location.assign(returnTo);
}

/* -------------------------------------------------------------------------
 * React context — small, hook-friendly surface for components.
 * ------------------------------------------------------------------------- */

export interface AuthContextValue {
  session: AuthSession | null;
  /** True while we're rehydrating from storage on first paint. */
  loading: boolean;
  signIn: (returnTo?: string) => void;
  signOut: (returnTo?: string) => Promise<void>;
  /** Called from /auth/callback after a successful exchange. */
  setSession: (s: AuthSession) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>.');
  }
  return ctx;
}
